// Thin composable that wires the real auth store, club hierarchy, and visiting
// context into the pure `buildNavModel` transform ([NAV-NO-ROUTE-INPUT]).
//
// Consumed by `AppBreadcrumbs.vue` for the breadcrumb base trail. It deliberately
// builds the district hierarchy with an EMPTY route context so the model stays
// route-independent - the invariant the pure builder guarantees structurally.
// (WorkspaceSidebar calls `buildNavModel` directly rather than this wrapper.)
//
// The VISITING scope is the one input the route has to supply, because a club
// the user does not belong to exists in no permission-derived list. Resolve it
// from the route here rather than reading only the session-persisted context:
// that context is written by `WorkspaceSidebar`, so depending on it alone left
// the bar blank wherever the sidebar had not written one - on every club page
// for an admin (its persistence watcher clears the context for them), and on
// any route that mounts no sidebar at all (issue #3210).

import { computed, watch, type ComputedRef } from "vue";
import { useRoute } from "vue-router";
import { useSubscriptionFeature } from "@/composables/useSubscriptionFeature";
import { useUserClubHierarchy, type VisitingHierarchyScope } from "@/composables/useUserClubHierarchy";
import { useVisitingClubContext } from "@/composables/useVisitingClubContext";
import { resolveClubRouteContext } from "@/core/utils/clubRouteContext";
import { isInactiveManagedClubPersonalDashboardEligible, useAuthStore } from "@/store/auth";
import { buildNavModel, type NavClubInput, type NavNode } from "./navModel";
import type { NavAuthContext } from "./navPermissions";

export function useNavModel(): { model: ComputedRef<NavNode[]> } {
  const authStore = useAuthStore();
  const hierarchy = useUserClubHierarchy();
  const visiting = useVisitingClubContext();
  const { isSubscriptionEnabled } = useSubscriptionFeature();
  const route = useRoute();

  const routeClubIdentifier = computed<string | undefined>(() => {
    const param = route.params.clubShortName;
    return (Array.isArray(param) ? param[0] : param) || undefined;
  });

  // Canonicalized when the user belongs to the club; the c<number> alias survives
  // untouched for a club they do not, which is the key `ensureExtraClub` /
  // `getVisitingClub` cache under.
  const activeClubShortName = computed<string | undefined>(
    () =>
      resolveClubRouteContext(routeClubIdentifier.value, authStore.user)?.clubShortName ?? routeClubIdentifier.value,
  );

  // Load the club's district/division/area so the trail resolves without a
  // sidebar having loaded it first. A club already in the hierarchy returns
  // from the shared module cache without a request.
  watch(
    activeClubShortName,
    (shortName) => {
      if (shortName) void hierarchy.ensureExtraClub(shortName);
    },
    { immediate: true },
  );

  const routeVisitingScope = computed<VisitingHierarchyScope | null>(() => {
    const identifier = activeClubShortName.value;
    const club = identifier ? hierarchy.getVisitingClub(identifier) : null;
    if (!club) return null;
    return {
      districtId: club.districtId,
      divisionId: club.divisionId,
      areaId: club.areaId,
      clubId: club.id,
      // Address the club by the identifier it was RESOLVED BY, not by the
      // canonical short name the backend reports. Only a membership lets
      // ClubWorkspaceLayout canonicalize the URL, so a visiting club stays on
      // whatever spelling the link used - the `c<number>` alias, or any other
      // form `Clubs.getClub` honours - and a node addressed differently matches
      // neither the active route nor the parent a leaf route hangs off.
      clubShortName: identifier,
      clubName: club.name,
      isOnToastHost: club.isOnToastHost,
    };
  });

  const model = computed<NavNode[]>(() => {
    const auth: NavAuthContext = {
      isAuthenticated: authStore.isAuthenticated,
      isAdmin: authStore.isAdmin,
      user: authStore.user,
    };

    const districts = hierarchy.buildDistrictGroups({});

    const scope = routeVisitingScope.value ?? visiting.context.value;
    const visitingDistrict = hierarchy.buildDistrictGroupForVisitingScope(scope);
    const visitingClub: NavClubInput | null =
      scope && scope.clubShortName && scope.clubName && typeof scope.clubId === "number"
        ? { id: scope.clubId, shortName: scope.clubShortName, name: scope.clubName, isOnToastHost: scope.isOnToastHost }
        : null;

    return buildNavModel({
      auth,
      districts,
      visitingDistrict,
      visitingClub,
      options: {
        subscriptionFeatureEnabled: isSubscriptionEnabled.value,
        forcePersonalDashboard: isInactiveManagedClubPersonalDashboardEligible(authStore.user),
      },
    });
  });

  return { model };
}
